import { useCallback, useRef, useState } from 'react'
import { useBooleanFlagValue } from '@openfeature/react-sdk'

import { getCurrentUser } from '@/selectors/user.selectors'
import { useReselector } from '@/utils/sharedHooks'
import { useAnalyticsTrack } from '@/features/amplitude'
import { FEATURE_FLAG_KEYS } from '@/flags'
import { NavAnnouncementProps } from '@/ui'
import { isNewUserForSidebarRestructure } from '@/utils/sidebarRolloutHelpers'
import { DeviceWidthCombo, useIsDeviceWidth } from '@/utils/deviceWidthHelpers'
import {
  dismissSidebarNavAnnouncement,
  isSidebarNavAnnouncementDismissed,
  recordSidebarNavAnnouncementShown,
  getSidebarNavAnnouncementTemporaryCloseCount,
  recordSidebarNavAnnouncementTemporarilyClosed,
} from './sidebarNavAnnouncementStorage'
import { SidebarNavAnnouncementStep } from './SidebarNavAnnouncementStep'
import { GlowingAnchorDot } from './GlowingAnchorDot'

const STEPS = [
  {
    anchorLabels: ['Transactions', 'Documents'],
    title: 'Your go-tos, front and center',
    body: 'Transactions and Documents now sit right at the top, one click away.',
  },
  {
    anchorLabels: ['Reports'],
    // Once Reports is expanded, the dot moves off the section header and
    // onto this specific nested page — the thing the copy actually refers to.
    nestedAnchorLabel: 'Profit & Loss',
    title: 'Profit & Loss is with your other reports now',
    body: 'Everything you use to read your numbers lives in one place.',
  },
  {
    anchorLabels: ['Tools'],
    nestedAnchorLabel: 'Allocation Guides',
    title: 'Say hello to Tools',
    body: 'Your Allocation Guides have moved into Tools, a new home for planning your practice finances.',
  },
]

// Permanent dismissal is tracked in localStorage (see
// sidebarNavAnnouncementStorage.ts), mirroring the feature-announcement
// pattern — not on the user record, since this data is meaningless once the
// walkthrough retires. The step's "Dismiss" button is a separate, unpersisted
// close — it hides the tour for the rest of this session without writing to
// storage, so it comes back on the next visit.
//
// Analytics: `viewed` fires once per exposure and is the denominator for reach;
// `advanced` fires on each non-final Next; `completed` fires when the last step
// is actioned (distinct from `temporarily closed`, so finishing the tour can't
// be mistaken for getting rid of it); `clicked ... cta` fires when the user
// follows an anchored nav item. Every event carries `step`, `show_count` and
// `total_steps` so the funnel can be sliced by step and by exposure, plus the
// `surface` the mobile card also stamps, so the two can be compared.
//
// `dismiss_count` is the running total of "Dismiss" presses. Completion is the
// only permanent exit, so without it a reluctant click-through looks identical
// to someone who read all three steps once. The final step has no Dismiss, so
// `temporarily closed` can only fire below `total_steps`.
export const useSidebarNavAnnouncement = (): NavAnnouncementProps => {
  const track = useAnalyticsTrack()
  const currentUser = useReselector(getCurrentUser)
  const [stepIndex, setStepIndex] = useState(0)
  const [isTemporarilyClosed, setIsTemporarilyClosed] = useState(false)
  // Labels (top-level items or nested pages) the user has clicked into —
  // their dot fades out and stays gone, independent of advancing the tour.
  const [visitedLabels, setVisitedLabels] = useState<Set<string>>(new Set())

  // Kill switch for the announcement alone: the redesign stays on when this is off.
  const sidebarNavAnnouncementEnabled = useBooleanFlagValue(
    FEATURE_FLAG_KEYS.sidebarNavAnnouncement,
    true
  )
  // Gated here, not just via the sidebar collapse, so this and the mobile card
  // are mutually exclusive by construction.
  const isSmallDevice = useIsDeviceWidth(DeviceWidthCombo.mobileOrTablet)
  const isExistingUser = Boolean(
    currentUser && !isNewUserForSidebarRestructure(currentUser.createdAt)
  )
  // Re-read every render, not subscribed: crossing from the mobile card takes
  // a width change, and that re-render re-runs this read.
  const persistentlyDismissed = isSidebarNavAnnouncementDismissed()
  const [sessionDismissed, setSessionDismissed] = useState(false)
  const shouldShow =
    sidebarNavAnnouncementEnabled &&
    isExistingUser &&
    !isSmallDevice &&
    !persistentlyDismissed &&
    !sessionDismissed
  const isOpen = shouldShow && !isTemporarilyClosed

  // Set when the tour first displays, then stamped onto every event for this
  // exposure. A ref rather than state — nothing renders it, and handlers need
  // the current value without waiting on a re-render.
  const showCountRef = useRef(0)
  const dismissCountRef = useRef(0)
  const hasTrackedViewRef = useRef(false)

  const trackAnnouncement = useCallback(
    (event: string, step: number, extraProps?: Record<string, unknown>) =>
      track(event, {
        user_id: currentUser?.id ?? null,
        step,
        show_count: showCountRef.current,
        dismiss_count: dismissCountRef.current,
        total_steps: STEPS.length,
        surface: 'desktop_coachmark',
        ...extraProps,
      }),
    [currentUser?.id, track]
  )

  // Driven by the sidebar rather than by `isOpen`, which only says the tour is
  // eligible — the sidebar collapses on small viewports, behind the Haven
  // panel, and on demand, and stops rendering the tour without telling us.
  // Always step 1: the tour can't be advanced before it has been displayed.
  const handleDisplay = useCallback(() => {
    if (hasTrackedViewRef.current) return
    hasTrackedViewRef.current = true
    showCountRef.current = recordSidebarNavAnnouncementShown()
    dismissCountRef.current = getSidebarNavAnnouncementTemporaryCloseCount()
    trackAnnouncement('viewed restructured sidebar nav announcement', 1)
  }, [trackAnnouncement])

  const handleAnchorItemClick = (label: string) => {
    // Only the first click on a given item is a click-through — after that its
    // dot is gone, so the announcement isn't what's driving the visit.
    if (!visitedLabels.has(label)) {
      trackAnnouncement(
        'clicked restructured sidebar nav announcement cta',
        stepIndex + 1,
        { cta_label: label }
      )
    }
    setVisitedLabels((prev) => new Set(prev).add(label))
  }

  const handleTemporaryClose = () => {
    dismissCountRef.current = recordSidebarNavAnnouncementTemporarilyClosed()
    trackAnnouncement(
      'temporarily closed restructured sidebar nav announcement',
      stepIndex + 1
    )
    setIsTemporarilyClosed(true)
  }

  const handleComplete = () => {
    trackAnnouncement(
      'completed restructured sidebar nav announcement',
      stepIndex + 1
    )
    dismissSidebarNavAnnouncement()
    setSessionDismissed(true)
  }

  const handleAdvance = () => {
    if (stepIndex < STEPS.length - 1) {
      trackAnnouncement(
        'advanced restructured sidebar nav announcement',
        stepIndex + 1
      )
      setStepIndex((prev) => prev + 1)
      return
    }
    handleComplete()
  }

  return {
    open: isOpen,
    activeStepIndex: stepIndex,
    onDisplay: handleDisplay,
    onAnchorItemClick: handleAnchorItemClick,
    renderAnchorIndicator: (label) => (
      <GlowingAnchorDot visible={!visitedLabels.has(label)} />
    ),
    steps: STEPS.map((step, index) => {
      const isLast = index === STEPS.length - 1
      return {
        anchorLabels: step.anchorLabels,
        nestedAnchorLabel: step.nestedAnchorLabel,
        content: (
          <SidebarNavAnnouncementStep
            title={step.title}
            body={step.body}
            actionLabel={
              isLast
                ? `Got it (${index + 1} of ${STEPS.length})`
                : `Next (${index + 1} of ${STEPS.length})`
            }
            onAction={handleAdvance}
            onDismiss={isLast ? undefined : handleTemporaryClose}
          />
        ),
      }
    }),
  }
}
